import React from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  CheckCircle2,
  Megaphone,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

export const ANNOUNCEMENT_ICONS = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
};

export const ANNOUNCEMENT_LABELS = {
  info: "Information",
  success: "Klart",
  warning: "Viktigt",
  error: "Varning",
};

export default function NativeAnnouncementDialog({ announcement, open, onOpenChange, onDismiss }) {
  const level = ANNOUNCEMENT_ICONS[announcement?.level] ? announcement.level : "info";
  const Icon = ANNOUNCEMENT_ICONS[level];
  const title = String(announcement?.title || "").trim() || "Meddelande";
  const message = String(announcement?.message || "").trim();
  const messageRef = React.useRef(null);
  const [hasScrollableContent, setHasScrollableContent] = React.useState(false);
  const [isAtScrollEnd, setIsAtScrollEnd] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const node = messageRef.current;
    if (!node) {
      setHasScrollableContent(false);
      setIsAtScrollEnd(false);
      return;
    }
    const scrollable = node.scrollHeight > node.clientHeight + 3;
    setHasScrollableContent(scrollable);
    setIsAtScrollEnd(scrollable && node.scrollTop + node.clientHeight >= node.scrollHeight - 3);
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(updateScrollState);
    const node = messageRef.current;
    if (typeof ResizeObserver === "undefined" || !node) {
      return () => window.cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(node);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [message, open, updateScrollState]);

  const scrollToBoundary = React.useCallback(() => {
    const node = messageRef.current;
    node?.scrollTo({ top: isAtScrollEnd ? 0 : node.scrollHeight, behavior: "smooth" });
  }, [isAtScrollEnd]);

  if (!announcement) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="native-v3-announcement-dialog" data-level={level}>
        <div className="native-v3-announcement-dialog-heading">
          <span className="native-v3-announcement-dialog-icon" aria-hidden="true"><Icon /></span>
          <span>{ANNOUNCEMENT_LABELS[level]}</span>
        </div>
        <DialogTitle className="native-v3-announcement-dialog-title">{title}</DialogTitle>
        <div className="native-v3-announcement-dialog-scroll">
          <DialogDescription asChild>
            <div ref={messageRef} className="native-v3-announcement-dialog-message" onScroll={updateScrollState}>{message}</div>
          </DialogDescription>
          {hasScrollableContent ? (
            <button
              type="button"
              className="native-v3-announcement-dialog-scroll-button"
              data-at-end={isAtScrollEnd ? "true" : "false"}
              onClick={scrollToBoundary}
              aria-label={isAtScrollEnd ? "Scrolla till början av meddelandet" : "Scrolla till slutet av meddelandet"}
            >
              <ArrowDown aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="native-v3-announcement-dialog-actions">
          {announcement?.dismissible && onDismiss ? (
            <button type="button" className="native-v3-announcement-dialog-secondary" onClick={() => onDismiss(announcement)}>
              Dölj meddelandet
            </button>
          ) : null}
          <button type="button" className="native-v3-announcement-dialog-primary" onClick={() => onOpenChange(false)}>
            Stäng
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
